/**
 * Admin API Service
 */
import { apiClient } from '../client';
import type {
  AdminUser,
  AdminUserListResponse,
  SuspendUserRequest,
  AssignPlanRequest,
  ImpersonationToken,
} from '../../types';

const BASE_PATH = '/api/admin';

export const adminService = {
  /**
   * List users with pagination and search
   */
  listUsers: async (params?: {
    page?: number;
    per_page?: number;
    search?: string;
    include_suspended?: boolean;
  }): Promise<AdminUserListResponse> => {
    const response = await apiClient.get<AdminUserListResponse>(`${BASE_PATH}/users`, { params });
    return response.data;
  },

  /**
   * Get user details
   */
  getUser: async (userId: number): Promise<AdminUser> => {
    const response = await apiClient.get<AdminUser>(`${BASE_PATH}/users/${userId}`);
    return response.data;
  },

  /**
   * Suspend a user
   */
  suspendUser: async (userId: number, data: SuspendUserRequest): Promise<{ message: string; user_id: number; suspended_at: string }> => {
    const response = await apiClient.post(`${BASE_PATH}/users/${userId}/suspend`, data);
    return response.data;
  },

  /**
   * Unsuspend a user
   */
  unsuspendUser: async (userId: number): Promise<{ message: string; user_id: number }> => {
    const response = await apiClient.post(`${BASE_PATH}/users/${userId}/unsuspend`);
    return response.data;
  },

  /**
   * Assign a plan to a user (sets admin override — Stripe won't revert this)
   */
  assignPlan: async (userId: number, data: AssignPlanRequest): Promise<{ message: string; user_id: number; plan_name: string }> => {
    const response = await apiClient.post(`${BASE_PATH}/users/${userId}/assign-plan`, data);
    return response.data;
  },

  /**
   * Clear admin plan override — hands control back to Stripe
   */
  clearPlanOverride: async (userId: number): Promise<{ message: string; user_id: number; plan_name: string; is_admin_override: boolean }> => {
    const response = await apiClient.post(`${BASE_PATH}/users/${userId}/clear-plan-override`);
    return response.data;
  },

  /**
   * Create impersonation token
   */
  impersonateUser: async (userId: number): Promise<ImpersonationToken> => {
    const response = await apiClient.post<ImpersonationToken>(`${BASE_PATH}/users/${userId}/impersonate`);
    return response.data;
  },

  /**
   * End impersonation session
   */
  endImpersonation: async (): Promise<{ message: string; admin_user_id: number }> => {
    const response = await apiClient.post(`${BASE_PATH}/impersonate/end`);
    return response.data;
  },

  /**
   * Get admin dashboard stats
   */
  getStats: async (): Promise<{
    total_users: number;
    active_users: number;
    suspended_users: number;
    total_qr_codes: number;
    total_scans: number;
    subscription_breakdown: { plan_id: number; count: number }[];
  }> => {
    const response = await apiClient.get(`${BASE_PATH}/stats`);
    return response.data;
  },

  /**
   * Send test email to self
   */
  sendTestEmail: async (template: 'welcome' | 'verification' | 'password_reset'): Promise<{ message: string }> => {
    const response = await apiClient.post(`${BASE_PATH}/test-email`, null, { params: { template } });
    return response.data;
  },
};
